import React from 'react';
import { Button, Tooltip } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { useQueryAsUser, useQueryCoordinate, useMutationAsUser } from '../../clients/graphql/graphql.hooks';
import { pilotTriggerHistoryQuery, jediAllocationAdjustmentHistoryGet } from '../../clients/graphql/components/tables-queries';
import { autoPilotSettingsUpdateAllocation } from '../../clients/graphql/views/over-allocation-queries';

interface UndoAllocationAdjustmentButtonProps {
  autoPilotSettingsKey: number;
  userKey: number;
  onSuccess?: () => void;
}

/**
 * Shows an "Undo" button only when the latest transaction on the tracker page
 * is an "Allocation Adjustment" with a negative amount (adjustment down).
 */
export const UndoAllocationAdjustmentButton: React.FC<UndoAllocationAdjustmentButtonProps> = ({
  autoPilotSettingsKey,
  userKey,
  onSuccess,
}) => {
  const { data: triggerData } = useQueryAsUser(pilotTriggerHistoryQuery, {
    userKey,
    variables: { userKey, autoPilotSettingsKey, limit: 20, offset: 0 },
  });

  const { data: allocationData } = useQueryCoordinate(jediAllocationAdjustmentHistoryGet, {
    variables: { autoPilotSettingsKey },
  });

  const [updateAllocation, { loading }] = useMutationAsUser(autoPilotSettingsUpdateAllocation, {
    userKey,
  });

  const triggers = triggerData?.triggerHistoryGet ?? [];
  const allocationAdjustments = allocationData?.jediAllocationAdjustmentHistoryGet?.allocationAdjustments ?? [];

  /**
   * Mirror the transaction table behavior:
   * - triggerHistoryGet rows (native trigger types)
   * - jediAllocationAdjustmentHistoryGet rows (rendered as ALLOCATION_ADJUSTMENT)
   *
   * Undo is allowed only if the latest row is ALLOCATION_ADJUSTMENT and amount < 0.
   */
  let undoAmount: number | null = null;

  const triggerRows = triggers
    .filter((item: { createdAt?: string; transactionAmount?: number | null; type?: string }) => item?.createdAt)
    .map((item: { createdAt: string; transactionAmount?: number | null; type?: string }) => ({
      createdAtMs: new Date(item.createdAt).getTime(),
      type: item.type ?? '',
      amount: item.transactionAmount ?? 0,
    }));

  const allocationAdjustmentRows = allocationAdjustments
    .filter((item: { createdAt?: string }) => item?.createdAt)
    .map((item: { createdAt: string; oldAllocationAmount: number; newAllocationAmount: number }) => ({
      createdAtMs: new Date(item.createdAt).getTime(),
      type: 'ALLOCATION_ADJUSTMENT',
      amount: item.newAllocationAmount - item.oldAllocationAmount,
    }));

  const latestRow = [...triggerRows, ...allocationAdjustmentRows].sort((a, b) => b.createdAtMs - a.createdAtMs)[0];

  if (latestRow?.type === 'ALLOCATION_ADJUSTMENT' && latestRow.amount < 0) {
    undoAmount = Math.abs(latestRow.amount);
  }

  const handleUndo = async () => {
    if (!undoAmount || undoAmount <= 0) return;
    try {
      await updateAllocation({
        variables: {
          input: {
            autoPilotSettingsKey,
            value: undoAmount,
            action: 'ADD',
          },
        },
      });
      onSuccess?.();
      window.location.reload();
    } catch (e) {
      console.error('Failed to undo allocation:', e);
    }
  };

  if (!undoAmount || undoAmount <= 0) {
    return null;
  }

  return (
    <Tooltip title={`Undo allocation adjustment of $${undoAmount.toLocaleString()}`}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<UndoIcon />}
        onClick={handleUndo}
        disabled={loading}
        color="secondary"
      >
        Undo allocation adjustment
      </Button>
    </Tooltip>
  );
};
